// JSON-LD Event structured data for registration-type assessments (Summit,
// CBL, etc.). Built once here so any current or future paid event gets valid
// schema.org markup for free — never hand-typed per event, never a second
// copy of the prices to drift from the pricing cards.
//
// eventConfig, tiers, and pricing shapes are loosely typed (`Record<string,
// unknown>`-ish) throughout the taker — App.tsx already reads eventConfig via
// `as any` for the same reason (the public config sanitizer's allowlist is
// the real contract, not a taker-side interface) — so this file matches that
// existing convention rather than inventing a stricter local type that would
// drift from the real shape.
//
// ★ eventEndDate does NOT mean "the event's final instant" — per
// configs/build-summit-configs.mjs's own comment, it means "day 2's start,
// same clock time as day 1, one day later." durationMinutes turns EACH day's
// start into that day's own end. So the JSON-LD `endDate` is
// eventEndDate + durationMinutes, not eventEndDate itself — using eventEndDate
// directly would understate the event by a full day's duration.

import type { Assessment } from '../types';

interface EventConfigLike {
  eventDate?: string;
  eventEndDate?: string;
  eventTimezone?: string;
  durationMinutes?: number;
  eventTitle?: string;
}

interface PricingTierLike {
  key?: string;
  title?: string;
  ctaUrl?: string;
  pricing?: Record<string, { amount?: number } | undefined>;
}

interface PricingTiersBlockLike {
  type?: string;
  baseCurrency?: string;
  tiers?: PricingTierLike[];
}

/** Build a schema.org Event JSON-LD object, or null when this assessment
 *  isn't a dated registration event (scorecards/quizzes/personality types,
 *  or a registration event with no eventConfig.eventDate set). */
export function buildEventJsonLd(assessment: Assessment): Record<string, unknown> | null {
  if (assessment.type !== 'registration') return null;

  const ec = (assessment.config as unknown as { eventConfig?: EventConfigLike }).eventConfig;
  if (!ec?.eventDate) return null;

  const startDate = ec.eventDate;
  const durationMs = (ec.durationMinutes ?? 480) * 60_000;
  // See file header: eventEndDate is day 2's START, not the event's end —
  // add that day's own duration to get the true final instant. Falls back to
  // startDate + one day's duration for a genuinely single-day event.
  const endAnchor = ec.eventEndDate ?? startDate;
  const endDate = new Date(new Date(endAnchor).getTime() + durationMs).toISOString();

  // Pull offers from the SAME pricingTiers block instance the page itself
  // renders (if any) — never a second, hand-maintained copy of the prices.
  const blocks = (assessment.config.intro?.blocks as PricingTiersBlockLike[] | undefined) ?? [];
  const pricingBlock = blocks.find((b) => b?.type === 'pricingTiers');
  const baseCurrency = pricingBlock?.baseCurrency ?? 'USD';
  const offers = (pricingBlock?.tiers ?? [])
    .map((t) => {
      const price = t.pricing?.[baseCurrency]?.amount;
      if (price === undefined) return null; // free tier — schema.org doesn't need a $0 Offer
      return {
        '@type': 'Offer',
        name: t.title ?? undefined,
        price: String(price),
        priceCurrency: baseCurrency,
        url: t.ctaUrl || undefined,
        availability: 'https://schema.org/InStock',
      };
    })
    .filter((o): o is NonNullable<typeof o> => o !== null);

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: ec.eventTitle ?? assessment.title,
    startDate,
    endDate,
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'VirtualLocation',
      url: typeof window !== 'undefined' ? window.location.href.split('?')[0] : undefined,
    },
    organizer: {
      '@type': 'Organization',
      name: assessment.brand?.name ?? 'The Successful Bookkeeper',
    },
    ...(offers.length > 0 ? { offers } : {}),
  };
}
